import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import ResponsiveDialog from '../../components/molecules/responsiveDialog';
import api from '../../services/api';
import { EmailChips } from './emailChips';
import MessageConfirmationDialog from './messageConfirmationDialog';
import { Title } from '../../components/atoms/title';
import { useUniversities } from '../../hooks/universities';

const baseSchema = yup
  .object({
    message: yup.string().required('A mensagem é obrigatória'),
    image_url: yup.string().nullable(),
  })
  .required();

const someUsersSchema = yup
  .object({
    emails: yup.array().min(1, 'Adicione pelo menos um e-mail').max(10000, 'Adicione no máximo 10.000 e-mails'),
  })
  .required()
  .concat(baseSchema);

const allUsersSchema = yup
  .object({
    after_date_time: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .test({
        name: 'max',
        message: 'Precisa ser antes da data seguinte',
        test(value, testContext) {
          if (testContext.parent.after_date_time && value) {
            return new Date(testContext.parent.before_date_time) > new Date(value);
          }
          return true;
        },
      })
      .max(new Date(), 'Data precisa ser no máximo agora :D'),
    before_date_time: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .min(new Date('2020-09-27'), 'Data precisa ser no mínimo depois do nascimento da Umatch')
      .test({
        name: 'max',
        message: 'Precisa ser depois da data anterior',
        test(value, testContext) {
          if (testContext.parent.before_date_time && value) {
            return new Date(testContext.parent.after_date_time) < new Date(value);
          }
          return true;
        },
      }),
    countries: yup.array().min(1, 'Selecione pelo menos um país'),
    locales: yup.array().min(1, 'Selecione pelo menos uma língua alvo'),
    start_time: yup
      .date()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .nullable()
      .min(new Date(), 'Data de envio precisa ser no futuro'),
    title: yup.string().required('O título é obrigatório'),
  })
  .required()
  .concat(baseSchema);

function MessagePreview(props) {
  const { message } = props;
  return <pre style={{ width: '40', overflow: 'auto' }}>{message}</pre>;
}

export function NotificationScheduler() {
  const [emails, setEmails] = useState([]);
  const { cities, countries, events, genders, locales, majors, states, subscriptions, universities } =
    useUniversities();

  const [selected, setSelected] = useState({
    cities: [],
    countries: [],
    events: [],
    genders: [],
    locales: [],
    majors: [],
    states: [],
    subscriptions: [],
    universities: [],
  });

  const [allUsers, setAllUsers] = useState(true);
  const [withNina, setWithNina] = useState(false);

  const [sendingMessage, setSendingMessage] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogContent, setErrorDialogContent] = useState('');
  const [confirmMessageOpen, setConfirmMessageOpen] = useState(false);

  const {
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      message: '',
      image_url: '',
      before_date_time: '',
      after_date_time: '',
      locales: [],
    },
    resolver: yupResolver(allUsers ? allUsersSchema : someUsersSchema),
  });

  useEffect(() => {
    setSelected(prev => ({
      ...prev,
      countries: [countries.find(c => c.name === 'Brazil')],
    }));
  }, [countries]);

  const handleAutocompleteSelection = fieldName => (_e, value, reason) => {
    if (reason === 'clear') {
      setSelected(prev => ({ ...prev, [fieldName]: [] }));
      return;
    }

    if (value !== null) {
      setSelected(prev => ({ ...prev, [fieldName]: value }));
    }
  };

  const handleChangeAllUsers = event => {
    if (event.target.value === 'true') {
      setAllUsers(true);
    } else {
      setAllUsers(false);
      setWithNina(true);
      setValue('title', 'Nova mensagem da Nina');
    }
  };

  const handleChangeWithNina = () => {
    if (withNina) {
      if (!allUsers) {
        toast('Nina não pode ser desativada para usuários específicos', { type: 'warning' });
      } else {
        setWithNina(false);
        setValue('title', '');
      }
    } else {
      setWithNina(true);
      setValue('title', 'Nova mensagem da Nina');
    }
  };

  const handleSendMessage = async data => {
    const { after_date_time, before_date_time, message, image_url, start_time, title } = data;

    let body;

    if (!allUsers) {
      body = {
        emails,
        message,
      };
    } else {
      body = {
        conditions: {
          after_timestamp: after_date_time === '' ? null : new Date(after_date_time).getTime(),
          before_timestamp: before_date_time === '' ? null : new Date(before_date_time).getTime(),
          cities: selected.cities.map(c => c.id),
          countries: selected.countries.map(c => c.id),
          events: selected.events.map(e => e.id),
          genders: selected.genders.map(g => g.value),
          locales: selected.locales.map(l => l.name),
          majors: selected.majors.map(m => m.id),
          states: selected.states.map(s => s.id),
          subscriptions: selected.subscriptions.map(s => s.name),
          universities: selected.universities.map(u => u.id),
        },
        message,
        image_url,
        start_time: DateTime.fromISO(start_time).toUTC().toISO(),
        title,
        with_nina: withNina,
      };
    }

    const route = allUsers ? '/admin/notifications' : '/admin/notifications/some';

    if (!allUsers && emails.length === 0) {
      toast('Nenhum e-mail foi adicionado para o envio.', { type: 'warning' });

      return;
    }

    setSendingMessage(true);

    try {
      await api.post(route, body);

      if (!allUsers) setEmails([]);

      reset();

      toast('Notificação criada com sucesso!', { type: 'success' });
    } catch (error) {
      setErrorDialogOpen(prevState => !prevState);
      setErrorDialogContent(error.response?.data.error?.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const onSubmit = () => setConfirmMessageOpen(true);
  const onMessageConfirmationClose = () => setConfirmMessageOpen(false);

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Title>Notificações & Mensagens</Title>
      </Grid>
      <Grid xs={12} item style={{ padding: '8px 16px' }}>
        <FormControl>
          <FormLabel id='destination-config'>Configurações</FormLabel>
          <RadioGroup aria-labelledby='destination-config' defaultValue name='radio-buttons-group'>
            <FormControlLabel
              checked={allUsers}
              control={<Radio />}
              label='Todos usuários'
              onChange={handleChangeAllUsers}
              value
            />
            <FormControlLabel
              checked={!allUsers}
              control={<Radio />}
              label='E-mails específicos'
              onChange={handleChangeAllUsers}
              value={false}
            />
          </RadioGroup>
          <FormControlLabel
            checked={withNina}
            control={<Switch />}
            label='Com mensagem da Nina'
            onChange={handleChangeWithNina}
          />
        </FormControl>
      </Grid>

      <Grid xs={12} item>
        <TextField
          {...register('title')}
          disabled={withNina}
          error={!!errors.title}
          fullWidth
          id='title'
          label={withNina ? '' : 'Título'}
          maxRows={2}
          multiline
          name='title'
          required
          variant='outlined'
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.message?.title}
        </Typography>
      </Grid>

      <Grid xs={12} item>
        <TextField
          {...register('message')}
          error={!!errors.message}
          fullWidth
          id='message'
          label='Mensagem'
          maxRows={15}
          minRows={4}
          multiline
          name='message'
          required
          variant='outlined'
        />

        <Typography variant='inherit' color='textSecondary'>
          {errors.message?.message}
        </Typography>
      </Grid>

      <Grid xs={12} item>
        <TextField
          {...register('image_url')}
          error={!!errors.image_url}
          fullWidth
          id='image_url'
          label='Imagem'
          maxRows={2}
          multiline
          name='image_url'
          variant='outlined'
        />

        <Typography variant='inherit' color='textSecondary'>
          {errors.message?.image_url}
        </Typography>
      </Grid>

      {!allUsers && (
        <Grid xs={12} item>
          <EmailChips emailItemsGet={list => setEmails(list)} />
        </Grid>
      )}

      {allUsers && (
        <>
          <Grid item md={6} xs={12}>
            <Autocomplete
              {...register('genders')}
              disableCloseOnSelect
              getOptionLabel={option => option.label}
              id='genders'
              multiple
              name='genders'
              options={genders}
              onChange={handleAutocompleteSelection('genders')}
              renderInput={params => <TextField {...params} variant='outlined' label='Gênero' />}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Autocomplete
              disableCloseOnSelect
              getOptionLabel={option => option?.local_description}
              id='locales'
              isOptionEqualToValue={(option, value) => option.name === value.name}
              multiple
              name='locales'
              onChange={(event, newValue) => {
                setValue('locales', newValue);
                handleAutocompleteSelection('locales')(event, newValue);
              }}
              options={locales.options}
              required
              renderInput={params => (
                <TextField
                  error={!!errors.locales}
                  helperText={errors.locales?.message}
                  label='Língua alvo'
                  required
                  variant='outlined'
                  {...params}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              {...register('states')}
              disableCloseOnSelect
              getOptionLabel={option => `${option.name} (${option.abbreviation})`}
              id='states'
              multiple
              name='states'
              onChange={handleAutocompleteSelection('states')}
              options={states}
              renderInput={params => <TextField {...params} variant='outlined' label='Estados' />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              defaultValue={[{ name: 'Brazil', id: 1 }]}
              fullWidth
              getOptionLabel={option => option?.name}
              id='countries'
              isOptionEqualToValue={(option, value) => option.id === value.id}
              label='País'
              multiple
              name='countries'
              onChange={(event, newValue) => {
                setValue('countries', newValue);
                handleAutocompleteSelection('countries')(event, newValue);
              }}
              options={countries}
              required
              variant='outlined'
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  variant='outlined'
                  label='País'
                  error={!!errors.countries}
                  helperText={errors.countries?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              {...register('universities')}
              disableCloseOnSelect
              getOptionKey={option => option.id}
              getOptionLabel={option => option.name}
              id='universities'
              multiple
              name='universities'
              onChange={handleAutocompleteSelection('universities')}
              options={universities}
              renderInput={params => <TextField {...params} variant='outlined' label='Universidades' />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...register('start_time')}
              error={!!errors.start_time}
              helperText={errors.start_time?.message}
              fullWidth
              id='start_time'
              InputLabelProps={{ shrink: true }}
              label='Hora de início'
              name='start_time'
              type='datetime-local'
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              {...register('majors')}
              disableCloseOnSelect
              getOptionLabel={option => option.name ?? 'CURSO PROIBIDO, NÃO SELECIONE'}
              getOptionKey={option => option.id}
              id='majors'
              multiple
              name='majors'
              onChange={handleAutocompleteSelection('majors')}
              options={majors}
              renderInput={params => <TextField {...params} variant='outlined' label='Cursos' />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              {...register('subscriptions')}
              disableCloseOnSelect
              getOptionLabel={option => option.description}
              id='subscriptions'
              multiple
              name='subscriptions'
              onChange={handleAutocompleteSelection('subscriptions')}
              options={subscriptions}
              renderInput={params => <TextField {...params} variant='outlined' label='Assinaturas' />}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              {...register('cities')}
              disableCloseOnSelect
              getOptionKey={option => option.id}
              getOptionLabel={option => option.name}
              id='cities'
              multiple
              name='cities'
              onChange={handleAutocompleteSelection('cities')}
              options={cities}
              renderInput={params => <TextField {...params} variant='outlined' label='Cidades' />}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              {...register('events')}
              disableCloseOnSelect
              getOptionKey={option => option.id}
              getOptionLabel={option => option.name}
              id='events'
              multiple
              name='events'
              onChange={handleAutocompleteSelection('events')}
              options={events}
              renderInput={params => <TextField {...params} variant='outlined' label='Eventos' />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              {...register('after_date_time')}
              InputLabelProps={{ shrink: true }}
              error={!!errors.after_date_time}
              fullWidth
              id='after_date_time'
              label='Entrou depois de'
              margin='dense'
              name='after_date_time'
              type='date'
              variant='outlined'
            />
            <Typography variant='inherit' color='textSecondary'>
              {errors.after_date_time?.message}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              {...register('before_date_time')}
              InputLabelProps={{ shrink: true }}
              error={!!errors.before_date_time}
              fullWidth
              id='before_date_time'
              label='Entrou antes de'
              margin='dense'
              name='before_date_time'
              type='date'
              variant='outlined'
            />

            <Typography variant='inherit' color='textSecondary'>
              {errors.before_date_time?.message}
            </Typography>
          </Grid>
        </>
      )}

      <Box m='auto' mt={3}>
        <Button color='primary' disabled={sendingMessage} onClick={handleSubmit(onSubmit)} variant='contained'>
          {sendingMessage ? 'Enviando...' : 'Enviar'}
        </Button>
      </Box>

      <MessageConfirmationDialog
        handleAction={() => handleSendMessage(getValues())}
        handleClose={onMessageConfirmationClose}
        message={() => MessagePreview({ message: getValues('message') })}
        open={confirmMessageOpen}
        title='Confirmar mensagem?'
        type='blue'
      />

      <ResponsiveDialog
        content={errorDialogContent}
        handleClose={() => setErrorDialogOpen(prevState => !prevState)}
        open={errorDialogOpen}
        title='Erro na criação'
        type='blue'
      />
    </Grid>
  );
}
